import { useLazyQuery } from "@apollo/client/react";
import { DSModal } from "@components/design-system/organisms";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  faFileAlt,
  faMagnifyingGlass,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth, useDebouncedValue } from "@hooks";
import { useTeam } from "@hooks/use-team";
import {
  NOTE_SEARCH_PLACEHOLDER,
  SEARCH_DEBOUNCE_TIME,
} from "@src/constants";
import mixpanel from "mixpanel-browser";
import { useEffect, useEffectEvent, useRef, useState } from "react";

import GET_INBOX_NOTES from "../../../../../graphql/queries/GetInboxNotes";
import NoteItem from "../../../../dashboard/dashboardInbox/NoteItem";

const SelectableNoteItem = ({ note, isSelected, onSelect }) => {
  return (
    <div
      className={`cursor-pointer rounded-standard transition-all ${
        isSelected
          ? "ring-2 ring-brand"
          : "hover:ring-2 hover:ring-brand/30"
      }`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(note);
      }}
      data-testid={`note-picker-item-${note.uuid}`}
    >
      <NoteItem
        note={note}
        hideNoteStatus
        handleActionWithPrevention={() => onSelect(note)}
      />
    </div>
  );
};

// Unmount content when hidden so state resets on each open
const NotePickerModal = ({ shouldShow, ...props }) => {
  if (!shouldShow) return null;
  return <NotePickerModalContent {...props} />;
};

const NotePickerModalContent = ({ hideModal, onSelectNote }) => {
  const { userUuid } = useAuth();
  const { currentTeam, selectedTeamMembers } = useTeam();
  const [selectedNote, setSelectedNote] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    SEARCH_DEBOUNCE_TIME,
  );
  const inputRef = useRef(null);

  const [fetchNotes, { data, loading }] = useLazyQuery(
    GET_INBOX_NOTES,
    {
      fetchPolicy: "no-cache",
    },
  );

  // Fetch notes on mount and when search query changes
  const fetchNotesEvent = useEffectEvent(() => {
    // Use "all" filter when searching (like GlobalSearchInput), "approved" otherwise
    const filter = debouncedSearchQuery ? "all" : "approved";
    fetchNotes({
      variables: {
        filter,
        limit: 20,
        offset: 0,
        teamUuid: currentTeam?.uuid || null,
        userUuids:
          selectedTeamMembers?.length > 0
            ? selectedTeamMembers
            : [userUuid],
        sortAscending: false,
        searchQuery: debouncedSearchQuery || null,
      },
    });
  });

  useEffect(() => {
    fetchNotesEvent();
  }, [
    currentTeam?.uuid,
    selectedTeamMembers,
    userUuid,
    debouncedSearchQuery,
  ]);

  const handleConfirm = () => {
    if (selectedNote) {
      mixpanel.track("Selected Real Note for Preview", {
        noteUuid: selectedNote.uuid,
        hasPatient: !!selectedNote?.patient?.name,
        jobType: selectedNote?.jobType,
      });
      onSelectNote(selectedNote);
      hideModal();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    inputRef.current?.focus();
  };

  const notes = data?.inboxNotes?.notes || [];

  // Filter notes that have transcription content
  const notesWithTranscription = notes.filter(
    (note) =>
      note?.transcription && note.transcription.trim().length > 0,
  );

  return (
    <DSModal
      isOpen
      onClose={hideModal}
      title="Select a Recent Note"
      subtitle="Choose a note to preview your template against. Only notes with transcriptions are shown."
      actions={[
        { label: "Cancel", variant: "cancel", onClick: hideModal },
        {
          label: "Use This Note",
          variant: "primary",
          onClick: handleConfirm,
          disabled: !selectedNote,
        },
      ]}
      width="w-[90vw] max-w-[700px]"
      dataTestId="note-picker-modal"
    >
      <div className="flex flex-col">
        {/* Search Input */}
        <div className="pb-sm">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-sm flex items-center pointer-events-none">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="text-placeholder absolute left-sm top-1/2 transform -translate-y-1/2"
              />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={NOTE_SEARCH_PLACEHOLDER}
              className="appearance-none block w-full px-sm py-sm border-2 bg-input border-input placeholder-placeholder text-primary rounded-large focus:outline-none focus:border-brand text-xs md:text-sm pl-10 pr-10 transition-all"
              data-testid="note-picker-search"
            />
            {searchQuery && (
              <div className="absolute inset-y-0 right-0 pr-sm flex items-center">
                <button
                  onClick={handleClearSearch}
                  className="text-placeholder hover:text-secondary transition-colors"
                  type="button"
                >
                  <FontAwesomeIcon
                    icon={faTimes}
                    className="text-sm"
                  />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Notes List */}
        <div className="flex flex-col gap-sm p-0.5">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-secondary">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="animate-pulse mr-sm"
              />
              {searchQuery ? "Searching..." : "Loading notes..."}
            </div>
          ) : notesWithTranscription.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-secondary gap-sm">
              <FontAwesomeIcon
                icon={faFileAlt}
                size="2x"
                className="text-placeholder"
              />
              {searchQuery ? (
                <>
                  <p>No notes found for &quot;{searchQuery}&quot;</p>
                  <button
                    onClick={handleClearSearch}
                    className="text-brand hover:opacity-80 text-sm"
                  >
                    Clear search
                  </button>
                </>
              ) : (
                <>
                  <p>
                    No notes found. You can still preview your
                    template against our pre-made samples.
                  </p>

                  <button
                    onClick={hideModal}
                    className="text-brand hover:opacity-80 text-sm"
                  >
                    Select a sample
                  </button>
                </>
              )}
            </div>
          ) : (
            notesWithTranscription.map((note) => (
              <SelectableNoteItem
                key={note.uuid}
                note={note}
                isSelected={selectedNote?.uuid === note.uuid}
                onSelect={setSelectedNote}
              />
            ))
          )}
        </div>
      </div>
    </DSModal>
  );
};

export default NotePickerModal;
